import { Labels } from "../../shared/Models/labels.model";
import {
    ActionItemAnalysisResponse,
    MeetingNoteAnalysisRequest,
    MeetingNoteAnalysisResponse
} from "../services/meetings.service";
import {Static} from "./static.model";

export class AttendeeRequestListModel {
    accountId: number;
}

export class Attendee {
    attendeeName: string;

    constructor(
        public accountId: number,
        public attendeeLogId: number | null = null,
        public attendeeId: number | null = null,
        public isAttendeeExpected: number | null = null,
        public didYouAttend: number | null = null,
        public attendeeDuration: number | null = null,
        public systemGenEfforts: number | null = null,
        public reminderAttendeeExpectedResponse: number | null = null,
        public imageUrl: string = "",
        attendeeName: string | null = null,
    ) {
        this.attendeeName = attendeeName || "N/A";
    }
}

export enum IsAttendeeExpectedEnum {
    Not_Joining = 0,
    Joining = 1,
    MayBe = 2,
}

export class GetNotificationsRequestBody {
    accountId: number | null;
    fromDate: string;
    toDate: string;
    createdAccountId: number | null = null;
    organizerAccountId: number | null = null;
    attendeeAccountIds: number[] | null = null;
}

export class UpdateAttendeeResponseRequestBody {
    constructor(
        public accountId: number,
        public meetingId: number,
        public isAttendeeExpected: number | null = null,
        public didYouAttend: number | null = null,
        public attendeeDuration: number | null = null,
    ) {}
}

/** Meeting workflow related Constants */
export class MeetingConstants {
    static readonly DEFAULT_MEETING_TYPE: string = "online";
    static readonly DEFAULT_REMINDER_TIME: number = 15;
    static readonly DEFAULT_DURATION: number = 60;
    static readonly JITSI_JOIN_DURATION: number = 30;
    static readonly RECURRING_OCCURRENCES_FETCH_LIMIT: number = 365;
    static readonly MEETING_TYPE_ONLINE: string = "online";
    static readonly MEETING_TYPE_OFFLINE: string = "offline";
    static readonly MEETING_TYPE_HYBRID: string = "hybrid";
    static readonly MEETING_START_TIME_SECONDS: string = "00";
    static readonly DEFAULT_MEETING_PROJECT_ID: number = 1;
    static readonly DEFAULT_MEETING_BU_ID: number = 1;
    static readonly MEETING_CREATED: string = "Meeting created successfully";
    static readonly MEETING_UPDATED: string = "Meeting updated successfully";
    static readonly MEETING_CANCELLED: string = "Meeting cancelled successfully";
    static readonly MEETING_ALREADY_CANCELLED: string = "Meeting is already cancelled";
    static readonly ERROR_IN_CANCEL_MEETING: string = "Error in cancelling meeting";
    static readonly DEFAULT_ACCOUNT_ID_TO_GET_MEETINGS: null = null;
    static readonly NOT_JOINING: string = "no";
    static readonly JOINING: string = "yes";
    static readonly MAY_BE: string = "maybe";
    static readonly UPDATE_MEETING_TO_SAVE_ACTION_ITEMS = "Please click on save button to save changes.";
    static readonly UPDATE_MEETING_AFTER_FETCHING =
        "After Successfully fetching meeting Summary and Action points, Click on save button to save changes.";
    static readonly DELETE_MEETING_TITLE = "Delete Meeting";
    static readonly DELETE_MEETING_MESSAGE = "Are you sure you want to delete this meeting?";
    static readonly FETCH_ERROR  = "There is problem in fetching."
    static readonly ANALYSIS_PROBLEM_MESSAGE = "A problem occurred while analysing this meeting. Please re-upload the file.";
}

// create enum from upcoming meeting constants
export enum UpcomingMeetingConstantsEnum {
    NOT_JOINING,
    JOINING,
    MAY_BE,
}

export interface MeetingLinkInfo {
    url: string | null;
    platform: string | null;
    label: string | null;
}

export class MeetingFormGroupFieldsConstants {
    static readonly MEETING_TYPE: string = "meetingType";
    static readonly REMINDER_TIME: string = "reminderTime";
    static readonly DURATION: string = "duration";
    static readonly ORGANIZER_ACCOUNT_ID: string = "organizerAccountId";
    static readonly ATTENDEE_REQUEST_LIST: string = "attendeeRequestList";
    static readonly TEAM_ID: string = "teamId";
    static readonly PROJECT_ID: string = "projectId";
    static readonly BU_ID: string = "buId";
    static readonly MEETING_TITLE: string = "title";
    static readonly MEETING_START_DATE: string = "startDate";
    static readonly MEETING_START_TIME: string = "startTime";
    static readonly MEETING_START_DATE_TIME: string = "startDateTime";
    static readonly MEETING_END_DATE_TIME: string = "endDateTime";
    static readonly MEETING_ORG_ID: string = "orgId";
    static readonly MEETING_VENUE: string = "venue";
    static readonly MEETING_AGENDA: string = "agenda";
    static readonly MEETING_KEY: string = "meetingKey";
    static readonly MEETING_CREATED_ACCOUNT_ID: string = "createdAccountId";
    static readonly MINUTES_OF_MEETING: string = "minutesOfMeeting";
    static readonly REFERENCE_TASK_NUMBER: string = "referenceTaskNumber";
    static readonly REFERENCED_MEETING_REASON_ID: string = "referencedMeetingReasonId";
    static readonly MEETING_FREQUENCY: string = "frequency";
}

export class RecurringMeetingFormGroupFieldsConstants {
    static readonly RECURRING_FREQUENCY_INDICATOR: string = "recurringFrequencyIndicator";
    static readonly RECURRING_MEETING_START_DATE_TIME: string = "recurringMeetingStartDateTime";
    static readonly RECURRING_MEETING_END_DATE_TIME: string = "recurringMeetingEndDateTime";
    static readonly RECUR_EVERY: string = "recurEvery";
    static readonly RECUR_DAYS: string = "recurDays";
}

export class ModifyMeetingDialogData {
    meeting: Meeting;
}

export class ModifyRecurrenceMeetingDialogData {
    recurringMeeting: RecMeeting;
}

export class AttendeeRequest {
    constructor(
        public attendeeLogId: number,
        public attendeeId: number,
        public accountId: number,
        public buId: number,
        public projectId: number,
        public teamId: number,
        public attendeeInvitationStatusId: number,
        public attendeeInvitationStatus: string,
        public isAttendeeExpected: number | null,
        public didYouAttend: number | null,
        public attendeeDuration: number | null,
        public reminderAttendeeExpectedResponse: number | null,
    ) {}
}

export class Meeting {
    constructor(
        public meetingType: string,
        public title: string,
        public organizerAccountId: number,
        public createdAccountId: number,
        public orgId: number,
        public teamId: number | null = null,
        public startDateTime: string,
        public meetingKey: string | null = null,
        public isExternalLink: boolean = false,
        public venue: string | null = null,
        public agenda: string | null = null,
        public endDateTime?: string,
        public reminderTime: number = MeetingConstants.DEFAULT_REMINDER_TIME,
        public duration: number = MeetingConstants.DEFAULT_DURATION,
        public attendeeRequestList: Attendee[] = [],
        public buId: number | null = null,
        public projectId: number | null = null,
        public minutesOfMeeting: null | string = null,
        public referenceEntityNumber: string | null = null,
        public referenceEntityTypeId: number | null = null,
        public labelsToAdd: string[] | null = null,
        public referencedMeetingReasonId: number | null = null,
        public updatedAccountId: number | null = null,
        public meetingId: number | null = null,
        public meetingNumber: string | null = null,
        public entityName: string | null = null,
        public recurringMeetingId: number | null = null,
        public createdDateTime: string | null = null,
        public lastUpdatedDateTime: string | null = null,
        public attendeeList: Attendee[] | null = null,
        public isCancelled: boolean | null = null,
        public actionItems: MeetingActionItemsModel[] = [],
        public modelFetchedList: MeetingModelFetched[] = [],
        public meetingNoteResponseList: MeetingNotesModel[] = [],
        public uploadFileForModelResponseList: UploadFileForModelResponse[] = [],
        public viewTranscription: boolean | null = null,
        public labels: Labels[] | null = [],
        public isEditable: boolean | null = null,
        public showUserPerceivedPercentage: boolean | null = null,
        public recurDays: string | null = null,
        public recurEvery: number | null = null,
        public canEditMeeting: boolean = false,
        public isFetched: boolean = true,
        public meetingLinkInfo: MeetingLinkInfo | null = null,
        public isAdhoc: boolean = false,
        public actualStartDateTime: string | null = null,
        public actualEndDateTime: string | null = null,
    ) {}
}

export function getMeetingTimeStatus(meeting: Meeting): string {
    const now = new Date();
    const start = new Date(meeting.startDateTime);
    const durationMinutes = meeting.duration ?? MeetingConstants.DEFAULT_DURATION;
    const end = new Date(start.getTime() + durationMinutes * 60000);

    if (now < start) {
        return Static.MeetingTimeStatus.UPCOMING;
    }
    if (now < end) {
        return Static.MeetingTimeStatus.LIVE;
    }
    return Static.MeetingTimeStatus.PAST;
}

export class MeetingInTaskView {
    meeting: Meeting;
    organizer: string;
    attendees: string[];
    currentUserAttendeeRequestList: Attendee | null;
    allRecordedEfforts: ExtendedAttendee[] | null = null;
    isAllowedToEdit: boolean | null = null;
}

export type AttendeeWithImage = {
    accountId: number;
    attendeeName: string;
    attendeeEmail: string;
    attendeeImage?: string; // Optional, as it may not be available initially
};

export class MeetingView {
    meeting: Meeting;
    organizer: string;
    attendees: AttendeeWithImage[];
    isUserAllowedToAddResponse: boolean;
    isUserRespondedYes: boolean;
    userAttendedDuration: number | null;
    currentUserAttendeeRequestList: Attendee | null;
    isAllowedToEdit: boolean | null = null;
    meetingTimeStatus: string | null;
}

export class RecentMeetingView {
    meeting: Meeting;
    organizer: string;
    attendees: string[];
    isUserAllowedToAddResponse: boolean;
    isUserRespondedYes: boolean;
    userAttendedDuration: number | null;
    currentUserAttendeeRequestList: Attendee | null;
    allRecordedEfforts: ExtendedAttendee[] | null = null;
    isAllowedToEdit: boolean | null = null;
    meetingTimeStatus: string | null;
}

export class ExtendedAttendee {
    accountId: number;
    attendeeLogId: number | null = null;
    attendeeId: number | null = null;
    isAttendeeExpected: number | null = null;
    didYouAttend: number | null = null;
    attendeeDuration: number | null = null;
    reminderAttendeeExpectedResponse: number | null = null;
    fullName: string | null = null;
    email: string | null = null;
}

export class UpcomingMeetingView {
    meeting: Meeting;
    organizer: string;
    attendees: string[];
    isUserAllowedToAddResponse: boolean;
    currentUserAttendeeRequestList: Attendee | null;
    isAllowedToEdit: boolean | null = null;
    meetingTimeStatus: string | null;
}

export class ExpendedViewResponse {
    constructor(
        public fromDate: string,
        public toDate: string,
        public meetingResponses: Meeting[],
    ) {}
}

export class CondensedMeeting {
    constructor(
        public title: string,
        public recurringMeetingsStartDates: string[],
        public endDateTime: string | null,
        public meetingId: number | null,
        public meetingNumber: string | null,
        public recurringMeetingId: number | null,
        public recurringMeetingNumber: string | null,
        public duration: string | null = null,
        public meetingKey: string | null = null,
        public venue: string | null = null,
        public agenda: string | null = null,
        public entityName: string | null = null,
        public attendeeRequestList: Attendee[] | null = null,
        public organizerAccountId: number | null = null,
        public createdAccountId: number | null = null,
        public orgId: number | null = null,
        public isCancelled?: boolean | null,
        public teamId?: number | null,
        public attendeeResponseList: Attendee[] | null = null,
    ) {}
}

export class CondensedMeetingView {
    meeting: CondensedMeeting;
    organizer: string;
    isUserHavePermissionsToEdit: boolean;
}

export class RecMeeting {
    constructor(
        public meetingKey: string,
        public isExternalLink: boolean = false,
        public organizerAccountId: number,
        public meetingType: string,
        public venue: string,
        public title: string,
        public meetingStartTime: string,
        public recurringFrequencyIndicator: number,
        public recurringMeetingStartDateTime: string,
        public recurringMeetingEndDateTime: string | null,
        public teamId: number | null,
        public recurDays: string | null = null,
        public recurEvery: any,
        public recurWeek: number | null = null,
        public agenda: string | null = null,
        public reminderTime: number = MeetingConstants.DEFAULT_REMINDER_TIME,
        public duration: number = MeetingConstants.DEFAULT_DURATION,
        public attendeeRequestList: Attendee[] | null = null,
        public createdAccountId: number,
        public orgId: number | null = null,
        public minutesOfMeeting: string | null = null,
        public projectId: number | null = null,
        public buId: number | null = null,
        public updatedAccountId: number | null = null,
        public endDateTime: any | null = null,
        public labelsToAdd: string[] | null = null,
        public recurringMeetingId?: number | null,
        public recurringMeetingNumber?: string | null,
        public numOfOccurrences?: number | null,
        public isCancelled?: boolean | null,
        public labels: Labels[] | null | undefined = [],
        public meetingLinkInfo: MeetingLinkInfo | null = null,
    ) {}
}

export class RecurringChildMeetingsList {
    recurringMeetingId: number;
    recurringMeetingNumber: string;
    recurringMeetingStartDateTime: string;
    recurringMeetingEndDateTime: string;
    numOfOccurrences: number;
    recurringFrequencyIndicator: number;
    recurDays: string;
    recurEvery: number;
    pageNumber: number;
    pageSize: number;
    totalMeetingsInList: number;
    totalPages: number;
    lastPage: boolean;
    meetingResponseList: Meeting[];
    isCancelled: boolean;
}

export class MeetingFiltersSelectChange {
    orgId: number | null | undefined;
    teamId: number | null | undefined;
    projectId: number | null | undefined;
    buId: number | null | undefined;
}

export interface UploadFileMetadataRequest {
    meetingId: number;
    orgId: number;
    modelId: number;
    meetingFileMetaDataList: {
        fileName: string;
        fileExtension: string;
        fileSize: number;
    }[];
    uploadedDateTime: string;
}

export interface UploadFileForModelResponse {
    meetingId: number;
    modelId: number;
    uploaderUserAccountDetails: UserAccountDetails;
    meetingFileMetaDataList: MeetingFileMetaData[];
    modelFetchedDtoList: MeetingModelFetched[];
    uploadedDateTime: string;
    underProcessing: boolean;
}

export class MeetingActionItemsModel {
    actionItem: string;
    postedByAccountId: number;
    actionItemId: number;
    modifiedByAccountId: number | null = null;
    createdDateTime: string | null = null;
    lastUpdatedDateTime: string | null = null;
    version: number;
    isDeleted: boolean;
    isUpdated: boolean;
    isCritical: boolean;
    taskId?: number | null;
    taskDetails?: TaskDetailsModel | null;
}

export class TaskDetailsModel {
    taskId: number;
    taskNumber: string;
    taskTypeId: number;
    assignedAccountId: number;
    assigneeEmail: string;
    percentageCompleted: number | null;
    statType: string | null;
    workflowTaskStatus: string;
}

export const ACTION_ITEM_CREATE_TASK_REQUEST_OBJECT = {
    mic: true,
    check_list: 1,
    model: 0,
    helpIndicator: 0,
    ins_id: 1
}

export interface MeetingModelFetched {
    modelId: number;
    isFetched: boolean;
    isProblem: boolean;
}

export class MeetingNotesModel {
    meetingNoteId: number;
    meetingNote: string;
    postedByAccountIdDetails: number;
    modifiedByAccountIdDetails: number | null;
    isImportant: boolean;
    isDeleted: boolean;
    version: number;
    createdDateTime: string;
    updatedDateTime: string;
    isUpdated: boolean;
}

export interface UserAccountDetails {
    email: string;
    accountId: number;
    firstName: string;
    lastName: string;
    isActive: boolean;
}

export interface MeetingFileMetaData {
    fileName: string;
    fileExtension: string;
    fileSize: number;
}

export interface MeetingAnalysisResponse {
    meetingId: number;
    modelId?: number | null;
    actionItemList: ActionItemAnalysisResponse[];
    meetingNoteList: MeetingNoteAnalysisResponse[];
    minutesOfMeeting?: string | null;
}

export interface MeetingAnalysisDetailsRequest {
    meetingId: number;
    modelId?: number | null;
    actionItemList?: ActionItemAnalysisRequest[];
    meetingNoteList?: MeetingNoteAnalysisRequest[];
    minutesOfMeeting?: string | null;
    meetingFileMetaDataList?: MeetingFileMetaData[];
}

export interface ActionItemAnalysisRequest {
    actionItemId?: number | null;
    actionItem: string;
    postedByAccountId: number;
    modifiedByAccountId?: number | null;
    isDeleted?: boolean | null;
    isImportant?: boolean;
    isUpdated?: boolean;
    version?: number | null;
}

export class MeetingOrganizerResponse {
    meetingId: number;
    hasMeetingStarted: boolean | null;
    hasMeetingEnded: boolean | null;
    actualStartDateTime: string | null;
    actualEndDateTime: string | null;
}

export enum ReferencedMeetingReasonEnum {
    UNCLEAR_REQUIREMENTS = 1,
    TECHNICAL_ISSUES,
    COLLABORATION,
    OTHER,
}

export type ReferencedMeetingReason = {
    value: ReferencedMeetingReasonEnum;
    label: string;
};

export const ReferencedMeetingReasonArray = [
    { value: ReferencedMeetingReasonEnum.UNCLEAR_REQUIREMENTS, label: "Requirements are not clear" },
    { value: ReferencedMeetingReasonEnum.TECHNICAL_ISSUES, label: "Facing some technical issues" },
    { value: ReferencedMeetingReasonEnum.COLLABORATION, label: "Collaboration meeting" },
    { value: ReferencedMeetingReasonEnum.OTHER, label: "Other" },
] as ReferencedMeetingReason[];

export class CreateMeetingPreFilledData {
    orgId: number | null | undefined = null;
    teamId: number | null | undefined = null;
    taskNumber: string | null | undefined = null;
}

export class Notes {
    answer: string;
    timeToRetrieveResponse: number;
    important: string[];

    constructor(answer: string, timeToRetrieveResponse: number) {
        this.answer = answer;
        this.timeToRetrieveResponse = timeToRetrieveResponse;
    }
}

export class ActionItems {
    answer: string;
    timeToRetrieveResponse: number;
    critical: string[];

    constructor(answer: string, timeToRetrieveResponse: number) {
        this.answer = answer;
        this.timeToRetrieveResponse = timeToRetrieveResponse;
    }
}

export class Summary {
    answer: string;
    timeToRetrieveResponse: number;
    summary: any;

    constructor(answer: string, timeToRetrieveResponse: number) {
        this.answer = answer;
        this.timeToRetrieveResponse = timeToRetrieveResponse;
    }
}

export class MeetingSummaryAndActionPoints {
    notes: Notes;
    actionItems: ActionItems;
    summary: Summary;

    constructor(notes: Notes, actionItems: ActionItems, summary: Summary) {
        this.notes = notes;
        this.actionItems = actionItems;
        this.summary = summary;
    }
}

export enum Meeting_Transcription_Model {
    LLAMA = "llama",
    CHATGPT = "chatgpt",
    BOTH = 'all',
}

export enum IsTranscriptionEnum {
    AUDIO_FILE,
    TEXT_FILE,
}

export const text_file_extensions = [
    "txt",
    "doc",
    "docx",
    "pdf",
    "rtf",
    "md",
    "pages",
    "srt",
    "vtt",
    "tex",
    "csv",
    "xlsx"
];

export const audio_file_extensions = [
    "wav",
    "aac",
    "mp3",
    "flac",
    "m4a",
    "m4b",
    "mpc",
    "ogg",
    "opus",
    "awb",
    "alac",
    "amr",
    "webm",
];

export const video_file_extensions = [
    "webm"
];

export class RecurringMeetSelection {
    dateAndIDPair: [number, string][];
    selectedMeeting: number | null;
    selectedDate: string;

    constructor() {
        this.dateAndIDPair = [];
        this.selectedMeeting = null;
        this.selectedDate = "";
    }
}

export function getValueOfAiTranscription(key: string): IsTranscriptionEnum | undefined {
    if (key in IsTranscriptionEnum) {
        // TypeScript treats enums as bidirectional maps, so we can access the value like this.
        return IsTranscriptionEnum[key as keyof typeof IsTranscriptionEnum];
    }
    return undefined; // Return undefined or handle the error if the key is not found in the enum
}

export function extractPoints(text: string): string[] {
    // Remove all '\n' characters
    let cleanedText = text.replace(/\n/g, " ");

    // Split the text using the pattern of numbers followed by a period, then skipping the space
    let points = cleanedText.split(/ *\d+\.\s/).filter((point) => point.trim() !== "");

    // Trim each point and return
    return points.map((point) => point.trim());
}

// allowed_extensions = {
//     'wav', 'aac', 'mp3', 'flac', 'm4a', 'm4b', 'mpc', 'ogg', 'opus', 'awb', 'alac', 'amr'
// } and .txt

export const allowed_extensions_for_trsancription = [
    "wav",
    "aac",
    "mp3",
    "flac",
    "m4a",
    "m4b",
    "mpc",
    "ogg",
    "opus",
    "awb",
    "alac",
    "amr",
    "webm",
    "mp4",
    "mov",
    "avi",
    "mkv",
    "wmv",
    "flv",
    "txt",
    "doc",
    "docx",
    "pdf",
    "rtf",
    "md",
    "pages",
    "srt",
    "vtt",
    "tex",
    "csv",
    "xlsx"
];
export const frequencies = ["Does not repeat", "Every", "Daily", "Weekly", "Custom"];

export class overallSearchMeetingResponse {
    meetingId: number;
    meetingNumber: string;
    title: string;
    organizerAccountId: number;
    meetingType: string;
    venue: string | null;
    meetingKey: string | null;
    startDateTime: string;
    endDateTime: string;
    teamId: number;
    buId: number;
    projectId: number;
    orgId: number;
    entityName: string | null = null;
    attendeAccountIdList: number[];
    labels: string[] | null;
    recurringMeetingId: number | null;
    recurDays: string | null;
    recurEvery: string | null;
}

export const MAX_ATTENDEES_TO_SHOW_WITH_PROFILE: number = 4;

export class InviteGuestJitsiRequest{
    firstName: string;
    lastName: string;
    email: string;
    expirationTime: number;
    roomName: string;
}
export interface NoteWithImportant {
    value: string;
    isIncludeInImportant: boolean;
}

export interface ActionItemWithCritical {
    value: string;
    isIncludeInCritical: boolean;
}

export enum MeetingDeleteType {
    SINGLE = 1,
    ALL = 2
}